/**
 * Schedule engine — extracted from Main.dc.html.
 *
 * IMPORTANT: These algorithms are a faithful translation of the prototype.
 * Do not "improve" them. The parity test confirms identical output.
 *
 * The engine is pure: it takes inputs and returns outputs with no side effects.
 * Both the API (Pages Functions) and the UI call the same functions.
 */

import {
  GROUPS, PORDER, RWG, NUM_BLOCKS,
  RELIEF1_ROT_WITH_EE1, SW0, SW1,
} from './constants';
import type { GroupDef } from './constants';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Slot = [groupIndex, postIndex]. */
export type Slot = [number, number];

/** Map of officerIndex → assigned Slot, or null if not posted. */
export type AssignmentMap = Record<number, Slot | null>;

export interface TrainingPairInput {
  ftoName: string;
  traineeName: string;
  startDay: number;       // day-of-month the pair started (prototype uses integers internally)
  vdaraDay: number;       // day 1–11 within the cycle when the pair goes to Vdara
  afterShift: string | null;
}

export interface TimeOffInput {
  name: string;
  a: number;  // start day-of-month
  b: number;  // end day-of-month
  type: string;
}

export interface OTInput {
  who: string;    // off-shift officer id like "d0", "g5"
  key: string;    // slot key like "0:7"
  a: number;      // start half-hour (0–47)
  b: number;      // end half-hour (0–48)
  h?: number;     // 1 if already in half-hour format
}

export interface PostSwapInput {
  who: number;    // officer index
  key: string;    // slot key
}

export interface ManualCoverInput {
  key: string;    // uncovered slot key
  mover: number;  // officer index
}

export interface WriterCoverInput {
  mover: number;  // officer index (must be a writer)
  key: string;    // slot key or "D:N" for dispatch console
}

export interface EngineInput {
  /** All officer names (base + extras), in order. Indices are officer IDs within the engine. */
  names: string[];
  /** Day of month being computed. */
  day: number;
  /** Generation count for this day. */
  gen: number;
  /** Indices of called-off officers. */
  calls: number[];
  /** Manual coverage assignments. */
  manual: ManualCoverInput[];
  /** Post swaps (from post panel). */
  postSwaps: PostSwapInput[];
  /** Manager closures (slot keys). */
  mgrClose: string[];
  /** Overtime entries. */
  ot: OTInput[];
  /** Writer/dispatch coverage. */
  wcov: WriterCoverInput[];
  /** CCI offline seats (indices). */
  cciOff: number[];
  /** Report writer indices (resolved from officer names). */
  rwIndices: number[];
  /** Officers moved to CCI (indices). */
  toCci: Record<number, boolean>;
  /** Training pairs. */
  pairs: TrainingPairInput[];
  /** FTO names that are turned off. */
  ftoOff: string[];
  /** Extra FTO names. */
  ftoExtra: string[];
  /** Approved time off entries. */
  timeOff: TimeOffInput[];
  /** Whether EE1 second officer is enabled. */
  ee1Two: boolean;
  /** Per-day state for history lookback: dayState[day] = { gen, ... }. */
  dayState: Record<number, { gen: number } | undefined>;
  /** CCI names (dispatchers). */
  cciNames: string[];
  /** Generation spans. */
  spans: { a: number; b: number }[];
  /** Off-shift officer name resolver. */
  offShiftNames: { day: string[]; grave: string[] };
  /** Overtime notes per off-shift officer. */
  otNotes: Record<string, string>;
}

export interface CallInfo {
  officerIndex: number;
  description: string;
}

export interface EngineOutput {
  /** Current assignment map after all cascading. */
  cur: Record<number, Slot | null>;
  /** Occupancy map: slotKey → officerIndex. */
  occ: Record<string, number>;
  /** Closed slots: slotKey → label ("P1", "Closed", "Short"). */
  closed: Record<string, string>;
  /** Moved officers: officerIndex → move info. */
  moved: Record<number, { from: string; forC?: number; console?: number; rwKey?: string }>;
  /** Call info: officerIndex → description. */
  callInfo: Record<number, string>;
  /** Called-off officer set. */
  calledSet: Record<number, boolean>;
  /** P-order info. */
  pInfo: Record<string, { caller: number; mover?: number }>;
  /** Uncovered slots after all cascading. */
  uncovered: { key: string; slot: Slot; caller: number }[];
  /** Overtime blocks: slotKey → [who, who, who, who] per block. */
  otBlocks: Record<string, (string | null)[]>;
  /** Overtime half-hour map: slotKey → 16-element array. */
  otHalf: Record<string, (string | null)[]>;
  /** OT applied entries. */
  otApplied: OTInput[];
  /** OT assignment: offShiftId → { key, a, b }. */
  otOf: Record<string, { key: string; a: number; b: number }>;
  /** Switched-in info from post swaps. */
  switchedIn: Record<string, { who: number; out: number | undefined; fk: string | null }>;
  /** Officers with closed-off posts. */
  closedOff: Record<number, string>;
  /** Writer away map: slotKey → officerIndex. */
  wAway: Record<string, number>;
  /** Writer cover applied. */
  wcovApplied: WriterCoverInput[];
  /** CCI cover: seatIndex → officerIndex. */
  cciCover: Record<number, number>;
  /** FTO pair context for the current day. */
  activePairs: TrainingPairInput[];
  /** Officers on offsite FTO day (at Vdara). */
  offsiteFto: Record<number, TrainingPairInput>;
  /** Trainee assignments. */
  trainee: Record<number, TrainingPairInput>;
  /** Officers off today (time off). */
  offToday: Record<number, TimeOffInput>;
  /** Officers not yet graduated. */
  notYet: Record<number, boolean>;
  /** Joined trainees when FTO calls off. */
  joined: Record<number, string[]>;
  /** Post swap log. */
  swapLog: { who: number; key: string }[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Slot key: "groupIndex:postIndex". */
export function slotKey(s: Slot): string {
  return s[0] + ':' + s[1];
}

/** P-order labels: slotKey → "P1", "P2", etc. */
const pLabelMap: Record<string, string> = {};
PORDER.forEach((s, k) => { pLabelMap[slotKey(s)] = 'P' + (k + 1); });

export function pLabel(k: string): string | undefined {
  return pLabelMap[k];
}

/** Display name: "Last, First" → "First Last". */
export function displayName(n: string): string {
  const p = n.split(', ');
  return p[1] + ' ' + p[0];
}

/** Initials from "Last, First". */
export function initials(n: string): string {
  const p = n.split(', ');
  return (p[1][0] + p[0][0]).toUpperCase();
}

/** Get groups with optional EE1 second officer post. */
export function getGroups(ee1Two: boolean): GroupDef[] {
  const groups = GROUPS.map(g => ({ ...g, posts: g.posts.slice() }));
  if (ee1Two) {
    groups[2].posts.push({ name: 'Employee Entrance 1', shortName: '2nd officer', held: false });
  }
  return groups;
}

/** Build rotation indices for each group (which posts rotate). */
export function buildRotOf(groups: GroupDef[], ee1Two: boolean): number[][] {
  const rotOf = groups.map(g => {
    const r: number[] = [];
    g.posts.forEach((p, k) => { if (!p.held) r.push(k); });
    return r;
  });
  if (ee1Two) {
    rotOf[2] = RELIEF1_ROT_WITH_EE1;
  }
  return rotOf;
}

/** Get the post index for a rotating post at a given block. */
export function postIdx(
  groups: GroupDef[], rotOf: number[][],
  g: number, r: number, b: number
): number {
  if (groups[g].posts[r].held) return r;
  const rot = rotOf[g];
  return rot[(rot.indexOf(r) + b) % rot.length];
}

/** Get the post definition for a rotating post at block b. */
export function postAt(
  groups: GroupDef[], rotOf: number[][],
  g: number, r: number, b: number
) {
  return groups[g].posts[postIdx(groups, rotOf, g, r, b)];
}

/** Build all slots (officer-assignable positions, groups 0–4). */
export function buildSlots(groups: GroupDef[]): Slot[] {
  const slots: Slot[] = [];
  for (let g = 0; g < 5; g++) {
    groups[g].posts.forEach((_, r) => { slots.push([g, r]); });
  }
  return slots;
}

/** Half-hour to time string: 28 → "1400", 48 → "2400". */
export function hhFormat(h: number): string {
  const hr = Math.floor(h / 2) % 24;
  const mn = h % 2 ? '30' : '00';
  return (h >= 48 ? '24' : (hr < 10 ? '0' : '') + hr) + mn;
}

/** Off-shift officer name from ID like "d0" or "g5". */
export function offShiftName(id: string, offShiftNames: { day: string[]; grave: string[] }): string | null {
  const sh = id.charAt(0) === 'd' ? 'day' : 'grave';
  return offShiftNames[sh][+id.slice(1)] || null;
}

// ─── Core: buildFor ─────────────────────────────────────────────────────────

interface BuildContext {
  off: Record<number, TimeOffInput>;
  tr: Record<number, TrainingPairInput>;
  os: Record<number, TrainingPairInput>;
  ap: TrainingPairInput[];
  notYet: Record<number, boolean>;
}

/**
 * Build the schedule for a given day and generation.
 * This is a faithful translation of Main.dc.html buildFor() (lines 1272–1328).
 *
 * @param day       Day of month
 * @param gen       Generation number
 * @param names     Full officer name list
 * @param groups    Post groups (with EE1 if applicable)
 * @param rotOf     Rotation index arrays
 * @param rwIndices Report writer officer indices
 * @param toCci     Officers moved to CCI dispatch
 * @param pairs     Training pairs
 * @param timeOff   Time off entries
 * @param dayState  Per-day state for history
 * @param ee1Two    Whether EE1 second officer is enabled
 */
export function buildFor(
  day: number,
  gen: number,
  names: string[],
  groups: GroupDef[],
  rotOf: number[][],
  rwIndices: number[],
  toCci: Record<number, boolean>,
  pairs: TrainingPairInput[],
  timeOff: TimeOffInput[],
  dayState: Record<number, { gen: number } | undefined>,
  ee1Two: boolean,
  memo: Record<string, AssignmentMap> = {},
): AssignmentMap {
  const mk = day + ':' + gen;
  if (memo[mk]) return memo[mk];

  const N = names.length;
  const slots = buildSlots(groups);
  const NS = slots.length;

  // Build context for this day
  const cx = buildContext(day, names, pairs, timeOff);

  // Seeded PRNG — exact same algorithm as prototype (line 1277)
  let s = (day * 1000 + gen + 1) >>> 0;
  const rnd = (): number => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const shuffle = <T>(arr: T[]): T[] => {
    for (let a = arr.length - 1; a > 0; a--) {
      const j = Math.floor(rnd() * (a + 1));
      const tmp = arr[a]; arr[a] = arr[j]; arr[j] = tmp;
    }
    return arr;
  };

  const map: AssignmentMap = {};

  // Report writers: shuffle eligible, seat the first 3
  const rw = shuffle(rwIndices.filter(i => !toCci[i] && !cx.off[i]));
  const seated = rw.slice(0, 3);

  // Build the officer order: non-RW, non-offsite, non-CCI, non-off, non-notYet
  let order: number[] = [];
  for (let y = 0; y < N; y++) {
    if (rwIndices.indexOf(y) < 0 && !cx.os[y] && !toCci[y] && !cx.off[y] && !cx.notYet[y]) {
      order.push(y);
    }
  }
  order = shuffle(order.concat(rw.slice(3)));

  // Sort by off-days (most off-days first for fairness)
  const BASE_RW = [9, 10, 11];
  const baseOthers: number[] = [];
  for (let x = 0; x < 32; x++) if (BASE_RW.indexOf(x) < 0) baseOthers.push(x);

  const base = (i: number, d: number): Slot | null => {
    if (i >= 32) return null;
    const k = BASE_RW.indexOf(i);
    if (k >= 0) return [RWG, (k + d) % 3];
    const pos = (baseOthers.indexOf(i) + 7 * d) % baseOthers.length;
    return pos < NS ? slots[pos] : null;
  };

  const genOf = (dd: number): number | null => {
    return dd === day ? gen : dayState[dd] ? dayState[dd]!.gen || 0 : null;
  };

  // hist: look up what an officer was assigned on a previous day
  const hist = (i: number, d: number): Slot | null => {
    const g = genOf(d);
    if (g === null) return base(i, d);
    const m = buildFor(d, g, names, groups, rotOf, rwIndices, toCci, pairs, timeOff, dayState, ee1Two, memo);
    return m[i] || null;
  };

  const offDays: Record<number, number> = {};
  order.forEach(i => {
    let o = 0;
    for (let d = 1; d < day; d++) {
      if ((i < 32 || genOf(d) !== null) && !hist(i, d)) o++;
    }
    offDays[i] = o;
  });
  order.sort((a, b) => offDays[b]! - offDays[a]!);

  const poolN = order.length;
  const open = slots.slice();

  // FTO pairs: each pair gets a new post each day
  cx.ap.forEach((tp, k) => {
    const fi = names.indexOf(tp.ftoName);
    if (!cx.tr[fi] || order.indexOf(fi) < 0) return;
    const pd = day - tp.startDay + 1;
    const want = (pd * 7 + k * 11 + gen * 13) % NS;
    let tries = 0;
    let at = -1;
    while (tries < NS) {
      const sl = slots[(want + tries) % NS];
      at = -1;
      open.forEach((o, oi) => { if (o[0] === sl[0] && o[1] === sl[1]) at = oi; });
      if (at >= 0) break;
      tries++;
    }
    if (at < 0) return;
    map[fi] = open[at]; open.splice(at, 1);
    order.splice(order.indexOf(fi), 1);
  });

  // Assign remaining officers to remaining open slots using fairness scoring
  order.forEach(i => {
    if (!open.length) { map[i] = null; return; }
    const gc = [0, 0, 0, 0, 0, 0];
    const pc = groups.map(g => g.posts.map(() => 0));
    for (let d = 1; d < day; d++) {
      const b0 = hist(i, d);
      if (!b0) continue;
      gc[b0[0]] += 4;
      for (let b = 0; b < 4; b++) pc[b0[0]][postIdx(groups, rotOf, b0[0], b0[1], b)]++;
    }
    let best = 0;
    let bestScore = 1e9;
    open.forEach((sl, idx) => {
      const share = (day - 1) * 4 * groups[sl[0]].posts.length / poolN;
      const score = (gc[sl[0]] - share) + pc[sl[0]][sl[1]] * 0.25 + rnd() * 3;
      if (score < bestScore) { bestScore = score; best = idx; }
    });
    map[i] = open[best];
    open.splice(best, 1);
  });

  // Seat report writers
  seated.forEach((i, k) => { map[i] = [RWG, k]; });

  memo[mk] = map;
  return map;
}

/** Build context for a specific day (time off, training pairs, offsite FTO). */
function buildContext(
  dd: number,
  names: string[],
  pairs: TrainingPairInput[],
  timeOff: TimeOffInput[],
): BuildContext {
  const pd = (p: TrainingPairInput) => dd - p.startDay + 1;
  const cx: BuildContext = { off: {}, tr: {}, os: {}, ap: [], notYet: {} };

  timeOff.forEach(t => {
    const ix = names.indexOf(t.name);
    if (ix >= 0 && dd >= t.a && dd <= t.b) cx.off[ix] = t;
  });

  pairs.forEach(p => {
    const d0 = pd(p);
    if (p.afterShift === 'swing' && d0 <= 11) {
      const tx = names.indexOf(p.traineeName);
      if (tx >= 0) cx.notYet[tx] = true;
    }
    if (d0 < 1 || d0 > 11) return;
    cx.ap.push(p);
    const fi = names.indexOf(p.ftoName);
    if (fi < 0) return;
    if (p.vdaraDay === d0) cx.os[fi] = p;
    else cx.tr[fi] = p;
  });

  return cx;
}

// ─── Core: applyCascade ─────────────────────────────────────────────────────

/**
 * Apply the full call-off → P-order → swap → closure → OT cascade.
 * Faithfully translates Main.dc.html lines 1331–1498.
 */
export function applyCascade(input: EngineInput): EngineOutput {
  const {
    names, day, gen, calls: rawCalls, manual, postSwaps: rawPostSwaps,
    mgrClose: rawMgrClose, ot: rawOt, wcov: rawWcov, cciOff: rawCciOff,
    rwIndices, toCci, pairs, ftoOff, ftoExtra, timeOff, ee1Two,
    dayState, cciNames, offShiftNames,
  } = input;

  const N = names.length;
  const groups = getGroups(ee1Two);
  const rotOf = buildRotOf(groups, ee1Two);
  const slots = buildSlots(groups);

  // Build FTO pair context
  const pairDay = (p: TrainingPairInput) => day - p.startDay + 1;
  const activePairs = pairs.filter(p => { const d = pairDay(p); return d >= 1 && d <= 11; });
  const offsiteFto: Record<number, TrainingPairInput> = {};
  const trainee: Record<number, TrainingPairInput> = {};

  activePairs.forEach(p => {
    let fi = names.indexOf(p.ftoName);
    if (fi < 0) return; // FTO not in roster
    if (p.vdaraDay === pairDay(p)) {
      offsiteFto[fi] = p;
    } else {
      trainee[fi] = p;
    }
  });

  // Time off for today
  const offToday: Record<number, TimeOffInput> = {};
  timeOff.forEach(t => {
    const ix = names.indexOf(t.name);
    if (ix >= 0 && day >= t.a && day <= t.b) offToday[ix] = t;
  });

  // Not-yet-graduated trainees
  const notYet: Record<number, boolean> = {};
  pairs.forEach(p => {
    const d0 = pairDay(p);
    if (p.afterShift === 'swing' && d0 <= 11) {
      const tx = names.indexOf(p.traineeName);
      if (tx >= 0) notYet[tx] = true;
    }
  });

  // Build today's assignments
  const memo: Record<string, AssignmentMap> = {};
  const todayMap = buildFor(day, gen, names, groups, rotOf, rwIndices, toCci, pairs, timeOff, dayState, ee1Two, memo);
  const today = { ...todayMap };

  // Filtered calls
  const calls = rawCalls.filter(c => c < N && !offToday[c]);

  // Initialize current state and occupancy
  const cur: Record<number, Slot | null> = {};
  const occ: Record<string, number> = {};
  for (let i0 = 0; i0 < N; i0++) {
    const s0 = today[i0] || null;
    cur[i0] = s0;
    if (s0) occ[slotKey(s0)] = i0;
  }

  const closed: Record<string, string> = {};
  let uncovered: { key: string; slot: Slot; caller: number }[] = [];
  const moved: Record<number, { from: string; forC?: number; console?: number; rwKey?: string }> = {};
  const callInfo: Record<number, string> = {};
  const calledSet: Record<number, boolean> = {};
  const pInfo: Record<string, { caller: number; mover?: number }> = {};

  const pq = PORDER.slice();

  // Spares: officers with no post who can cover
  const spares: number[] = [];
  for (let i1 = 0; i1 < N; i1++) {
    if (!cur[i1] && calls.indexOf(i1) < 0 && !offsiteFto[i1] && !toCci[i1] && !offToday[i1] && !notYet[i1]) {
      spares.push(i1);
    }
  }

  // Call-off cascade
  calls.forEach(c => {
    calledSet[c] = true;
    const s = cur[c];
    if (!s) { callInfo[c] = 'was not posted, nothing moves'; return; }
    const k = slotKey(s);
    delete occ[k]; cur[c] = null;

    // Try spares first
    if (spares.length) {
      const sp = spares.shift()!;
      occ[k] = sp; cur[sp] = s;
      moved[sp] = { from: 'spare', forC: c };
      callInfo[c] = displayName(names[sp]) + ' (not posted) takes ' + postAt(groups, rotOf, s[0], s[1], 0).name + ' · no post closed';
      return;
    }

    // Check if the called-off officer was on a P post
    for (let a = 0; a < pq.length; a++) {
      if (slotKey(pq[a]) === k) {
        closed[k] = pLabelMap[k]!;
        pInfo[k] = { caller: c };
        pq.splice(a, 1);
        callInfo[c] = 'was on ' + pLabelMap[k] + ' · ' + pLabelMap[k] + ' closed, nobody moves';
        return;
      }
    }

    // Find a P-officer to move
    let mover: number | null = null;
    let pk: string | null = null;
    while (pq.length && mover === null) {
      const kk = slotKey(pq.shift()!);
      if (occ[kk] !== undefined) { mover = occ[kk]; pk = kk; }
    }
    if (mover === null) {
      uncovered.push({ key: k, slot: s, caller: c });
      callInfo[c] = 'P1–P5 already used · manager picks who covers ' + postAt(groups, rotOf, s[0], s[1], 0).name;
      return;
    }
    delete occ[pk!]; closed[pk!] = pLabelMap[pk!]!;
    pInfo[pk!] = { caller: c, mover };
    occ[k] = mover; cur[mover] = s;
    moved[mover] = { from: pLabelMap[pk!]!, forC: c };
    callInfo[c] = displayName(names[mover]) + ' moves from ' + pLabelMap[pk!] + ' to ' + postAt(groups, rotOf, s[0], s[1], 0).name + ' · ' + pLabelMap[pk!] + ' closed';
  });

  // Trainee reassignment when FTO calls off
  const joined: Record<number, string[]> = {};
  calls.forEach(c => {
    const tp = trainee[c] || offsiteFto[c];
    if (!tp || (input.toCci[c])) return; // tOff check simplified
    let host: number | null = null;
    Object.keys(trainee).forEach(u => {
      const ui = +u;
      if (host === null && ui !== c && !calledSet[ui] && cur[ui]) host = ui;
    });
    if (host !== null) {
      joined[host] = (joined[host] || []).concat([tp.traineeName]);
      callInfo[c] = (callInfo[c] || '') + ' · trainee ' + displayName(tp.traineeName) + ' joins FTO ' + displayName(names[host]);
    } else {
      callInfo[c] = (callInfo[c] || '') + ' · trainee ' + displayName(tp.traineeName) + ' needs an FTO';
    }
  });

  // Manual coverage
  manual.forEach(m => {
    let ui = -1;
    for (let a = 0; a < uncovered.length; a++) if (uncovered[a].key === m.key) ui = a;
    const ms = cur[m.mover];
    if (ui < 0 || !ms || calledSet[m.mover]) return;
    const u = uncovered[ui];
    const mk = slotKey(ms);
    delete occ[mk]; closed[mk] = 'Closed';
    occ[u.key] = m.mover; cur[m.mover] = u.slot;
    moved[m.mover] = { from: 'manager', forC: u.caller };
    callInfo[u.caller] = displayName(names[m.mover]) + ' moved by manager · ' + postAt(groups, rotOf, ms[0], ms[1], 0).name + ' closed';
    uncovered.splice(ui, 1);
  });

  // Post swaps
  const filteredPostSwaps = rawPostSwaps.filter(m => m.who < N);
  const switchedIn: Record<string, { who: number; out: number | undefined; fk: string | null }> = {};
  const swapLog: { who: number; key: string }[] = [];

  filteredPostSwaps.forEach(m => {
    const w = m.who;
    if (calledSet[w] || toCci[w] || offsiteFto[w]) return;
    const from = cur[w] || null;
    const fk = from ? slotKey(from) : null;
    if (fk === m.key) return;
    const tg = m.key.split(':').map(Number) as Slot;
    const o = occ[m.key];
    if (fk) delete occ[fk];
    occ[m.key] = w; cur[w] = tg;
    const mw = moved[w];
    if (o !== undefined) {
      cur[o] = from; if (fk) occ[fk] = o;
      moved[w] = moved[o]; if (mw) moved[o] = mw; else delete moved[o];
      if (!moved[w]) delete moved[w];
    } else {
      delete closed[m.key];
      for (let a = 0; a < uncovered.length; a++) if (uncovered[a].key === m.key) { uncovered.splice(a, 1); break; }
      if (fk) closed[fk] = 'Closed';
    }
    switchedIn[m.key] = { who: w, out: o, fk };
    swapLog.push({ who: w, key: m.key });
  });

  // Manager closures
  const mgrClosed = rawMgrClose.filter(k => {
    const t = k.split(':').map(Number);
    return groups[t[0]] && groups[t[0]].posts[t[1]];
  });
  const closedOff: Record<number, string> = {};
  mgrClosed.forEach(k => {
    const o = occ[k];
    if (o !== undefined) { delete occ[k]; cur[o] = null; delete moved[o]; closedOff[o] = k; }
    closed[k] = 'Closed';
    for (let a = 0; a < uncovered.length; a++) if (uncovered[a].key === k) { uncovered.splice(a, 1); break; }
  });

  // Auto-close: any empty post not already closed or uncovered
  const shortKeys: Record<string, boolean> = {};
  uncovered.forEach(u => { shortKeys[u.key] = true; });
  for (let gS = 0; gS < groups.length; gS++) {
    groups[gS].posts.forEach((_, rS) => {
      const kS = gS + ':' + rS;
      if (occ[kS] === undefined && closed[kS] === undefined && !shortKeys[kS]) {
        closed[kS] = 'Short';
      }
    });
  }

  // Writer/dispatch coverage
  const wAway: Record<string, number> = {};
  const wcovApplied: WriterCoverInput[] = [];
  const cciCover: Record<number, number> = {};
  const cciOff = rawCciOff.filter(i => i < cciNames.length);

  rawWcov.forEach(w => {
    const ms = cur[w.mover];
    if (!ms || ms[0] !== RWG || calledSet[w.mover]) return;
    if (w.key.charAt(0) === 'D') {
      const ci = +w.key.slice(2);
      if (cciOff.indexOf(ci) < 0 || cciCover[ci] !== undefined) return;
      const mk2 = slotKey(ms);
      delete occ[mk2]; wAway[mk2] = w.mover; cciCover[ci] = w.mover; cur[w.mover] = null;
      moved[w.mover] = { from: 'cci', console: ci, rwKey: mk2 };
      wcovApplied.push(w);
      return;
    }
    if (occ[w.key] !== undefined) return;
    let ui = -1;
    for (let a = 0; a < uncovered.length; a++) if (uncovered[a].key === w.key) ui = a;
    if (closed[w.key] === undefined && ui < 0) return;
    if (ui >= 0) {
      callInfo[uncovered[ui].caller] = displayName(names[w.mover]) + ' (report writer) covers';
      uncovered.splice(ui, 1);
    }
    delete closed[w.key];
    const mk = slotKey(ms);
    delete occ[mk]; wAway[mk] = w.mover;
    occ[w.key] = w.mover; cur[w.mover] = w.key.split(':').map(Number) as Slot;
    moved[w.mover] = { from: 'writer', forC: undefined, rwKey: mk };
    wcovApplied.push(w);
  });

  // Overtime
  const otBlocks: Record<string, (string | null)[]> = {};
  const otHalf: Record<string, (string | null)[]> = {};
  const otOf: Record<string, { key: string; a: number; b: number }> = {};
  const otApplied: OTInput[] = [];

  rawOt.forEach(o => {
    let a0 = o.a, b0 = o.b;
    if (!o.h) { a0 = SW0 + 4 * (o.a || 0); b0 = SW0 + 4 * (o.b === undefined ? 4 : o.b); }
    if (!offShiftName(o.who, offShiftNames) || otOf[o.who] || occ[o.key] !== undefined || b0 <= a0) return;
    const isUnc = uncovered.some(u => u.key === o.key);
    if (closed[o.key] === undefined && !isUnc) return;
    const hs = otHalf[o.key] || new Array(16).fill(null);
    const lo = Math.max(a0, SW0), hi = Math.min(b0, SW1);
    for (let x = lo; x < hi; x++) if (hs[x - SW0]) return;
    for (let y = lo; y < hi; y++) hs[y - SW0] = o.who;
    otHalf[o.key] = hs; otOf[o.who] = { key: o.key, a: a0, b: b0 };
    otApplied.push({ who: o.who, key: o.key, a: a0, b: b0, h: 1 });
  });

  Object.keys(otHalf).forEach(k => {
    const hs = otHalf[k]!;
    const bl: (string | null)[] = [null, null, null, null];
    let any = false;
    for (let b = 0; b < 4; b++) {
      const cnt: Record<string, number> = {};
      for (let q = 0; q < 4; q++) { const w = hs[b * 4 + q]; if (w) cnt[w] = (cnt[w] || 0) + 1; }
      let best: string | null = null;
      Object.keys(cnt).forEach(w => { if (!best || cnt[w] > cnt[best]) best = w; });
      bl[b] = best; if (best) any = true;
    }
    if (any) otBlocks[k] = bl;
  });

  // Filter uncovered: fully covered by OT
  uncovered = uncovered.filter(u => {
    const hs = otHalf[u.key];
    const full = !!hs && hs.every(z => !!z);
    if (full) callInfo[u.caller] = 'covered on overtime';
    return !full;
  });

  return {
    cur, occ, closed, moved, callInfo, calledSet, pInfo,
    uncovered, otBlocks, otHalf, otApplied, otOf, switchedIn,
    closedOff, wAway, wcovApplied, cciCover,
    activePairs, offsiteFto, trainee, offToday, notYet, joined,
    swapLog,
  };
}

// ─── Rotation Fairness ──────────────────────────────────────────────────────

export interface FairnessRow {
  officerIndex: number;
  name: string;
  groupCounts: number[];  // blocks spent in each group
  totalBlocks: number;
}

/**
 * Calculate rotation fairness for the month up to the given day.
 * Only officer-post days count (not RW days).
 * Matches Main.dc.html fairness tab logic.
 */
export function computeFairness(
  upToDay: number,
  names: string[],
  groups: GroupDef[],
  rotOf: number[][],
  rwIndices: number[],
  toCci: Record<number, boolean>,
  pairs: TrainingPairInput[],
  timeOff: TimeOffInput[],
  dayState: Record<number, { gen: number } | undefined>,
  ee1Two: boolean,
): FairnessRow[] {
  const memo: Record<string, AssignmentMap> = {};
  const rows: FairnessRow[] = [];

  for (let i = 0; i < names.length; i++) {
    if (toCci[i]) continue;
    const gc = new Array(groups.length).fill(0);
    let total = 0;
    for (let d = 1; d <= upToDay; d++) {
      const g = d === upToDay ? dayState[d]?.gen ?? null : dayState[d]?.gen ?? null;
      if (g === null) continue;
      const m = buildFor(d, g, names, groups, rotOf, rwIndices, toCci, pairs, timeOff, dayState, ee1Two, memo);
      const s = m[i];
      if (!s || s[0] === RWG) continue; // RW days don't count
      for (let b = 0; b < NUM_BLOCKS; b++) {
        gc[s[0]]++;
        total++;
      }
    }
    rows.push({ officerIndex: i, name: names[i], groupCounts: gc, totalBlocks: total });
  }

  return rows;
}
